// Contenuti delle schede progetto (pagina dedicata per singolo progetto).
// Bilingue IT/EN. Le immagini di sezione usano foto della gallery come
// segnaposto: sostituire i numeri con quelli scelti dal cliente.

export const PROJECT_DETAILS = {
  "relax-in-cascina": {
    slug: "relax-in-cascina",
    hero: "/assets/projects/relax-in-cascina.webp",
    it: {
      name: "Relax in Cascina",
      tagline: "Recupero e ristrutturazione di un cascinale nel Monferrato",
      intro:
        "Recuperare un cascinale significa trovare un equilibrio tra la storia dell'edificio e un nuovo modo di abitarlo. Con Relax in Cascina, Project Works affronta il recupero di un edificio rurale del territorio piemontese con l'obiettivo di valorizzarne il carattere originario e, allo stesso tempo, creare spazi adatti alle esigenze contemporanee. Il progetto nasce dalla lettura dell'edificio esistente: geometrie, materiali, aperture, rapporto con gli spazi esterni e con il paesaggio diventano elementi da interpretare e valorizzare, anziché semplici vincoli da superare.",
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "L'intervento parte dalla volontà di recuperare il carattere del fabbricato esistente e trasformarlo in un'abitazione capace di offrire comfort, funzionalità e una relazione più diretta con il contesto circostante.",
            "Prima di intervenire è stato quindi fondamentale comprendere l'edificio, le sue caratteristiche costruttive e le possibilità di trasformazione. Da questa analisi si è sviluppato il progetto, cercando un dialogo tra architettura esistente e nuovi elementi, senza cancellare l'identità del luogo.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/ante-1.webp",
            "/assets/projects/relax-in-cascina/ante-2.webp",
            "/assets/projects/relax-in-cascina/ante-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Conoscere prima di progettare",
          paragraphs: [
            "Ogni intervento su un edificio esistente parte da un rilievo accurato. La conoscenza dello stato di fatto permette di ricostruire correttamente geometrie e caratteristiche dell'immobile e costituisce la base sulla quale sviluppare tutte le successive decisioni progettuali.",
            "Nel recupero di edifici rurali questo passaggio assume particolare importanza perché strutture costruite e modificate nel tempo possono presentare geometrie e situazioni molto diverse da quelle di un edificio di nuova costruzione.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/tetto.webp",
            "/assets/projects/relax-in-cascina/igloo.webp",
            "/assets/projects/relax-in-cascina/facciata.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il rapporto tra esistente e nuovo",
          paragraphs: [
            "L'obiettivo del progetto non è trasformare il cascinale in qualcosa che non gli appartiene. Gli elementi caratteristici dell'edificio vengono utilizzati come punto di partenza per introdurre nuovi spazi e nuove funzioni.",
            "La progettazione ricerca quindi un equilibrio tra conservazione, trasformazione e comfort contemporaneo, mantenendo leggibile il carattere dell'architettura originaria.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/solaio-legno.webp",
            "/assets/projects/relax-in-cascina/tetto-ristrutturato.webp",
            "/assets/projects/relax-in-cascina/cappotto.webp",
          ],
          layout: "grid",
        },
        {
          title: "Materiali e dettagli",
          paragraphs: [
            "Nel recupero di un cascinale i materiali contribuiscono in modo determinante all'identità dell'intervento. La scelta delle finiture e dei nuovi elementi viene quindi sviluppata cercando coerenza con l'architettura esistente e con il contesto, evitando contrasti puramente decorativi e privilegiando soluzioni capaci di accompagnare il carattere dell'edificio.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/coppi-piemontesi.webp",
            "/assets/projects/relax-in-cascina/opus-incertum.webp",
            "/assets/projects/relax-in-cascina/terrazzo.webp",
          ],
          layout: "grid",
        },
        {
          title: "Dal progetto al cantiere",
          paragraphs: [
            "La qualità di una ristrutturazione dipende anche dalla capacità di trasformare le scelte progettuali in soluzioni realmente costruibili. Per questo Project Works può seguire le diverse fasi dell'intervento, dal rilievo e dalla progettazione fino alle pratiche tecniche e alla direzione dei lavori.",
            "Un unico percorso consente di mantenere continuità tra ciò che viene progettato e ciò che viene realizzato.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/cantiere-1.webp",
            "/assets/projects/relax-in-cascina/cantiere-2.webp",
            "/assets/projects/relax-in-cascina/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "Il progetto restituisce nuova vita al cascinale, preservandone l'identità e adattandolo alle esigenze dell'abitare contemporaneo. Il risultato nasce dal dialogo tra ciò che era già presente e ciò che è stato introdotto attraverso il progetto.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/risultato-1.webp",
            "/assets/projects/relax-in-cascina/risultato-2.webp",
            "/assets/projects/relax-in-cascina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Stai pensando di recuperare un cascinale nel Monferrato?",
        text: "Acquistare o ristrutturare un cascinale richiede una valutazione che tenga insieme potenzialità architettoniche, stato dell'edificio, aspetti urbanistici e catastali e fattibilità dell'intervento. Project Works può seguire il percorso dalla prima analisi dell'immobile fino alla progettazione e alla realizzazione.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "Relax in Cascina",
      tagline: "Recovery and renovation of a farmhouse in Monferrato",
      intro:
        "Recovering a farmhouse means finding a balance between the building's history and a new way of living in it. With Relax in Cascina, Project Works takes on the recovery of a rural building in the Piedmont countryside, aiming to enhance its original character while creating spaces suited to contemporary needs. The project starts from a reading of the existing building: geometries, materials, openings, the relationship with outdoor spaces and the landscape become elements to interpret and enhance, rather than simple constraints to overcome.",
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The intervention stems from the desire to recover the character of the existing building and transform it into a home capable of offering comfort, functionality and a more direct relationship with its surroundings.",
            "Before intervening, it was essential to understand the building, its construction features and its potential for transformation. The project developed from this analysis, seeking a dialogue between existing architecture and new elements, without erasing the identity of the place.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/ante-1.webp",
            "/assets/projects/relax-in-cascina/ante-2.webp",
            "/assets/projects/relax-in-cascina/ante-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Knowing before designing",
          paragraphs: [
            "Every intervention on an existing building starts with an accurate survey. Knowing the existing state makes it possible to correctly reconstruct the building's geometry and features, and forms the basis on which all subsequent design decisions are developed.",
            "In the recovery of rural buildings this step is particularly important, because structures built and modified over time can present geometries and conditions very different from those of a new building.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/tetto.webp",
            "/assets/projects/relax-in-cascina/igloo.webp",
            "/assets/projects/relax-in-cascina/facciata.webp",
          ],
          layout: "grid",
        },
        {
          title: "The relationship between existing and new",
          paragraphs: [
            "The goal of the project is not to turn the farmhouse into something that doesn't belong to it. The building's characteristic elements are used as a starting point to introduce new spaces and new functions.",
            "The design therefore seeks a balance between conservation, transformation and contemporary comfort, keeping the character of the original architecture legible.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/solaio-legno.webp",
            "/assets/projects/relax-in-cascina/tetto-ristrutturato.webp",
            "/assets/projects/relax-in-cascina/cappotto.webp",
          ],
          layout: "grid",
        },
        {
          title: "Materials and details",
          paragraphs: [
            "In the recovery of a farmhouse, materials play a decisive role in the identity of the intervention. The choice of finishes and new elements is therefore developed in search of coherence with the existing architecture and the context, avoiding purely decorative contrasts and favouring solutions able to accompany the character of the building.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/coppi-piemontesi.webp",
            "/assets/projects/relax-in-cascina/opus-incertum.webp",
            "/assets/projects/relax-in-cascina/terrazzo.webp",
          ],
          layout: "grid",
        },
        {
          title: "From design to construction site",
          paragraphs: [
            "The quality of a renovation also depends on the ability to turn design choices into truly buildable solutions. This is why Project Works can follow the different phases of the intervention, from survey and design to technical permits and construction management.",
            "A single path maintains continuity between what is designed and what is built.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/cantiere-1.webp",
            "/assets/projects/relax-in-cascina/cantiere-2.webp",
            "/assets/projects/relax-in-cascina/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "The project gives new life to the farmhouse, preserving its identity and adapting it to the needs of contemporary living. The result comes from the dialogue between what was already there and what was introduced through the project.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/risultato-1.webp",
            "/assets/projects/relax-in-cascina/risultato-2.webp",
            "/assets/projects/relax-in-cascina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Thinking of renovating a farmhouse in Monferrato?",
        text: "Buying or renovating a farmhouse requires an assessment that brings together architectural potential, the building's condition, planning and cadastral aspects and the feasibility of the intervention. Project Works can follow the entire path, from the first analysis of the property through to design and construction.",
        button: "Tell us about your project",
      },
    },
  },
  "casa-sulla-collina": {
    slug: "casa-sulla-collina",
    hero: "/assets/projects/casa-sulla-collina.webp",
    it: {
      name: "La Casa sulla Collina",
      tagline: "Demolizione e ricostruzione di un rustico nell'Alto Monferrato",
      intro:
        "Un nuovo edificio che nasce dalla memoria di quello esistente. La Casa sulla Collina è un intervento di demolizione e ricostruzione di un vecchio rustico nell'Alto Monferrato, ai confini con la Langa Astigiana. Il progetto nasce con l'obiettivo di recuperare la volumetria del fabbricato originario, integrandola con gli ampliamenti consentiti dalla normativa urbanistica piemontese, per realizzare un'abitazione di circa 330 m² con pertinenze e piscina.",
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "L'intervento parte dalla volontà di realizzare un nuovo edificio mantenendo un forte legame con l'architettura rurale piemontese e con il paesaggio nel quale si inserisce.",
            "La demolizione del vecchio rustico e il recupero della volumetria esistente hanno permesso di ripensare completamente l'abitazione, integrando gli ampliamenti consentiti dalla normativa urbanistica e adattando gli spazi alle esigenze dell'abitare contemporaneo.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/progetto-1.webp",
            "/assets/projects/casa-sulla-collina/progetto-2.webp",
            "/assets/projects/casa-sulla-collina/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recuperare l'identità del luogo",
          paragraphs: [
            "Ricostruire non significa necessariamente cancellare ciò che esisteva.",
            "Il progetto ripropone all'esterno e negli elementi strutturali materiali e caratteri costruttivi legati alla tradizione piemontese, reinterpretandoli all'interno di un edificio completamente nuovo.",
            "L'obiettivo è mantenere riconoscibile il rapporto con il territorio e con l'architettura rurale originaria, pur introducendo nuove funzioni e una diversa organizzazione degli spazi.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/identita-1.webp",
            "/assets/projects/casa-sulla-collina/identita-2.webp",
            "/assets/projects/casa-sulla-collina/identita-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Tra passato e presente",
          paragraphs: [
            "L'architettura esterna dialoga con la tradizione costruttiva piemontese, mentre gli spazi interni accolgono elementi e soluzioni contemporanee.",
            "Materiali, proporzioni e dettagli sono stati studiati per creare un equilibrio tra questi due linguaggi, evitando una semplice riproduzione del passato.",
            "Il risultato è un'abitazione nella quale tradizione e contemporaneità convivono, mantenendo leggibile il carattere del luogo.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/passato-1.webp",
            "/assets/projects/casa-sulla-collina/passato-2.webp",
            "/assets/projects/casa-sulla-collina/passato-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il rapporto con il paesaggio",
          paragraphs: [
            "La posizione collinare rappresenta uno degli elementi caratterizzanti dell'intervento.",
            "Situata nell'Alto Monferrato, ai confini con la Langa Astigiana, la casa instaura un rapporto diretto con il paesaggio circostante.",
            "Gli spazi esterni, le pertinenze e la piscina completano il progetto e diventano parte integrante dell'abitazione, mettendo in relazione architettura e territorio.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/paesaggio-1.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-2.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "La Casa sulla Collina nasce dalla trasformazione radicale di un vecchio rustico, ma conserva un legame riconoscibile con il luogo dal quale proviene.",
            "Il recupero della volumetria, l'impiego di materiali legati alla tradizione costruttiva locale e l'introduzione di elementi contemporanei hanno permesso di creare una nuova abitazione capace di mettere in relazione passato, presente e paesaggio.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/risultato-1.webp",
            "/assets/projects/casa-sulla-collina/risultato-2.webp",
            "/assets/projects/casa-sulla-collina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Stai pensando di recuperare o trasformare un immobile nel Monferrato?",
        text: "Il recupero di un rustico richiede una valutazione che tenga insieme caratteristiche dell'edificio, possibilità urbanistiche, inserimento nel paesaggio e obiettivi del progetto. Project Works può seguire il percorso dalla prima analisi dell'immobile fino alla progettazione e alla realizzazione.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "La Casa sulla Collina",
      tagline: "Demolition and reconstruction of a country house in Alto Monferrato",
      intro:
        "A new building born from the memory of the existing one. La Casa sulla Collina is a demolition and reconstruction project of an old country house in Alto Monferrato, on the border with the Langa Astigiana. The project was born with the aim of recovering the volume of the original building, integrating it with the extensions permitted by Piedmontese planning regulations, to create a home of about 330 m² with outbuildings and a swimming pool.",
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The intervention stems from the desire to create a new building while maintaining a strong bond with Piedmontese rural architecture and the landscape in which it sits.",
            "The demolition of the old country house and the recovery of the existing volume made it possible to completely rethink the home, integrating the extensions permitted by planning regulations and adapting the spaces to the needs of contemporary living.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/progetto-1.webp",
            "/assets/projects/casa-sulla-collina/progetto-2.webp",
            "/assets/projects/casa-sulla-collina/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recovering the identity of the place",
          paragraphs: [
            "Rebuilding does not necessarily mean erasing what existed.",
            "The project re-proposes, on the outside and in the structural elements, materials and construction characters linked to the Piedmontese tradition, reinterpreting them within a completely new building.",
            "The goal is to keep the relationship with the territory and the original rural architecture recognisable, while introducing new functions and a different organisation of spaces.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/identita-1.webp",
            "/assets/projects/casa-sulla-collina/identita-2.webp",
            "/assets/projects/casa-sulla-collina/identita-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Between past and present",
          paragraphs: [
            "The external architecture dialogues with the Piedmontese building tradition, while the interior spaces welcome contemporary elements and solutions.",
            "Materials, proportions and details were studied to create a balance between these two languages, avoiding a simple reproduction of the past.",
            "The result is a home in which tradition and contemporaneity coexist, keeping the character of the place legible.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/passato-1.webp",
            "/assets/projects/casa-sulla-collina/passato-2.webp",
            "/assets/projects/casa-sulla-collina/passato-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The relationship with the landscape",
          paragraphs: [
            "The hillside position is one of the defining elements of the intervention.",
            "Located in Alto Monferrato, on the border with the Langa Astigiana, the house establishes a direct relationship with the surrounding landscape.",
            "The outdoor spaces, the outbuildings and the swimming pool complete the project and become an integral part of the home, connecting architecture and territory.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/paesaggio-1.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-2.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "La Casa sulla Collina was born from the radical transformation of an old country house, but preserves a recognisable bond with the place it comes from.",
            "The recovery of the volume, the use of materials linked to the local building tradition and the introduction of contemporary elements have made it possible to create a new home capable of connecting past, present and landscape.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/risultato-1.webp",
            "/assets/projects/casa-sulla-collina/risultato-2.webp",
            "/assets/projects/casa-sulla-collina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Thinking of recovering or transforming a property in Monferrato?",
        text: "Recovering a country house requires an assessment that brings together the building's characteristics, planning possibilities, landscape integration and the project's goals. Project Works can follow the entire path, from the first analysis of the property through to design and construction.",
        button: "Tell us about your project",
      },
    },
  },
};
